import { useState, useRef, useEffect, useCallback } from "react";
import { io as socketIO } from "socket.io-client";
import { Terminal, Activity, Zap, ShieldAlert, LogOut, Radio, Server, Trash2, ToggleRight, BarChart2, Globe } from 'lucide-react';

const API = "/api";

const apiFetch = async (path, options = {}, token = null) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  
  const res = await fetch(`${API}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
  
  if (!res.ok) { 
    if (res.status === 401) {
      localStorage.removeItem("admin_tf_token");
      localStorage.removeItem("admin_tf_user");
      window.location.reload(); 
    }
    const err = await res.json().catch(() => ({ error: "Netzwerkfehler" })); 
    throw new Error(err.error || "Unbekannter Fehler"); 
  }
  return res.json();
};

const THEME = {
  bg: "#05070B", panel: "#0A0D15", border: "#1A2131", text: "#E0E5F0", textMuted: "#6C7C9E", 
  accent: "#0ECFD9", warn: "#FF4757", success: "#34D399"
};

// --- LOGIN SCREEN ---
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const d = await apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
      if (d.user.role !== "Admin") throw new Error("Zugriff verweigert. Nur für Flotten-Admins.");
      onLogin(d.token, d.user);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:"100vh", background:THEME.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"monospace", color:THEME.text}}>
      <div style={{width: 400, background:THEME.panel, padding: 40, borderRadius: 12, border:`1px solid ${THEME.border}`, boxShadow:`0 0 40px rgba(14,207,217,0.1)`}}>
        <div style={{textAlign:"center", marginBottom: 30}}>
          <ShieldAlert size={48} color={THEME.warn} style={{marginBottom: 16}} />
          <h1 style={{fontSize: 20, fontWeight: 700, letterSpacing: "0.1em", margin:0}}>TF COMMAND CENTER</h1>
          <p style={{fontSize: 12, color: THEME.textMuted, marginTop: 8}}>RESTRICTED ACCESS ONLY</p>
        </div>
        {error && <div style={{background:"#FF475715", border:"1px solid #FF475740", color:"#FF4757", padding:12, borderRadius:8, fontSize:12, marginBottom:20}}>{error}</div>}
        <form onSubmit={submit} style={{display:"flex", flexDirection:"column", gap:16}}>
          <div>
            <div style={{fontSize:10, color:THEME.textMuted, marginBottom:6}}>ADMIN ID</div>
            <input type="text" value={username} onChange={e=>setUsername(e.target.value)} style={{width:"100%", background:"#000", border:`1px solid ${THEME.border}`, color:THEME.accent, padding:"12px", borderRadius:6, outline:"none"}} autoFocus />
          </div>
          <div>
            <div style={{fontSize:10, color:THEME.textMuted, marginBottom:6}}>AUTHORIZATION CODE</div>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} style={{width:"100%", background:"#000", border:`1px solid ${THEME.border}`, color:THEME.accent, padding:"12px", borderRadius:6, outline:"none"}} />
          </div>
          <button type="submit" disabled={loading} style={{background:THEME.accent, color:"#000", border:"none", padding:14, borderRadius:6, fontWeight:700, cursor:"pointer", marginTop:10, fontSize:14}}>{loading ? "VERIFYING..." : "INITIALIZE CONNECTION"}</button>
        </form>
      </div>
    </div>
  );
};

// --- MAIN ADMIN APP ---
export default function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_tf_token"));
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem("admin_tf_user")); } catch { return null; } });
  
  const [activeTab, setActiveTab] = useState("deploy");
  const [deployTime, setDeployTime] = useState("");
  const [deployLogs, setDeployLogs] = useState([]);
  const [notification, setNotification] = useState("");
  const [radarNodes, setRadarNodes] = useState([]);
  const [radarLoading, setRadarLoading] = useState(false);
const [remoteNodes, setRemoteNodes] = useState([]);
const [analyticsData, setAnalyticsData] = useState(null);
  
  // 🚀 Erweitert: Liste für vergangene Broadcasts
  const [broadcastForm, setBroadcastForm] = useState({ title: "", message: "", type: "info", show_in_ticker: true });
  const [pastBroadcasts, setPastBroadcasts] = useState([]);

  const socketRef = useRef(null);
  const terminalRef = useRef(null);

  const notify = msg => { setNotification(msg); setTimeout(() => setNotification(""), 3000); };

  const handleLogin = (tok, usr) => {
    setToken(tok); setUser(usr);
    localStorage.setItem("admin_tf_token", tok);
    localStorage.setItem("admin_tf_user", JSON.stringify(usr));
  };

  const logout = () => {
    localStorage.removeItem("admin_tf_token"); localStorage.removeItem("admin_tf_user");
    setToken(null); setUser(null);
  };

  // 🚀 Lade alte Nachrichten, wenn der Tab gewechselt wird
  useEffect(() => {
    if (activeTab === "broadcast" && token) {
      apiFetch("/broadcasts", {}, token).then(setPastBroadcasts).catch(() => {});
    }

if (activeTab === "toggles" && token) {
      apiFetch("/admin/clients/features", {}, token).then(setRemoteNodes).catch(() => {});
    }

if (activeTab === "analytics" && token) {
      apiFetch("/admin/analytics", {}, token).then(setAnalyticsData).catch(e => notify("Analytics Fehler: " + e.message));
    }
  }, [activeTab, token]);

  useEffect(() => {
    if (!token) return;
    socketRef.current = socketIO({ auth: { token }, path: '/api/socket.io' });
    
    socketRef.current.on('deployment_log', (logLine) => {
      setDeployLogs(prev => [...prev, logLine]);
    });

    return () => { if(socketRef.current) socketRef.current.disconnect(); };
  }, [token]);

  useEffect(() => {
    if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [deployLogs]);

// Lade Radar-Daten alle 5 Sekunden, wenn der Tab offen ist
  useEffect(() => {
    let interval;
    if (activeTab === "radar" && token) {
      setRadarLoading(true);
      const fetchRadar = () => {
        apiFetch("/admin/radar", {}, token)
          .then(data => { setRadarNodes(data); setRadarLoading(false); })
          .catch(e => console.error("Radar Sync Error:", e.message));
      };
      fetchRadar(); // Sofort erster Aufruf
      interval = setInterval(fetchRadar, 5000); // Dann alle 5 Sekunden
    }
    return () => clearInterval(interval);
  }, [activeTab, token]);

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  const executeDeployment = async () => {
    if (!window.confirm(`Flotten-Update ${deployTime ? 'für diesen Zeitpunkt planen' : 'SOFORT ausrollen'}?`)) return;
    try {
      setDeployLogs([]);
      const res = await apiFetch("/admin/deploy", { method: "POST", body: JSON.stringify({ scheduledFor: deployTime || null }) }, token);
      notify(res.message);
      setDeployTime(""); 
    } catch(e) { notify("FEHLER: " + e.message); }
  };

  return (
    <div style={{minHeight:"100vh", background:THEME.bg, color:THEME.text, fontFamily:"'Segoe UI', system-ui, sans-serif", display:"flex"}}>
      {notification && <div style={{position:"fixed", top:20, right:20, background:THEME.accent, color:"#000", padding:"12px 24px", borderRadius:8, fontWeight:700, zIndex:999, boxShadow:`0 0 20px ${THEME.accent}40`}}>{notification}</div>}

      {/* SIDEBAR */}
      <div style={{width: 260, background:THEME.panel, borderRight:`1px solid ${THEME.border}`, display:"flex", flexDirection:"column"}}>
        <div style={{padding: 24, borderBottom:`1px solid ${THEME.border}`, display:"flex", alignItems:"center", gap:12}}>
          <Server color={THEME.accent} size={28} />
          <div>
            <div style={{fontWeight:800, letterSpacing:"0.05em"}}>MOTHERSHIP</div>
            <div style={{fontSize:11, color:THEME.success, display:"flex", alignItems:"center", gap:4}}><div style={{width:6,height:6,borderRadius:"50%",background:THEME.success}}/> SYSTEM ONLINE</div>
          </div>
        </div>

        <div style={{flex:1, padding:16, display:"flex", flexDirection:"column", gap:8}}>
          {[
            { id: "deploy", icon: <Terminal size={18}/>, label: "Fleet Deployment" },
            { id: "broadcast", icon: <Radio size={18}/>, label: "Global Broadcast" },
            { id: "toggles", icon: <ToggleRight size={18}/>, label: "Remote Config" },
            { id: "analytics", icon: <BarChart2 size={18}/>, label: "Global Analytics" },
            { id: "radar", icon: <Activity size={18}/>, label: "Telemetry Radar " },
          ].map(t => (
            <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{background: activeTab===t.id ? THEME.border : "transparent", border:"none", color: activeTab===t.id ? THEME.text : THEME.textMuted, padding:"12px 16px", borderRadius:8, display:"flex", alignItems:"center", gap:12, cursor:"pointer", fontWeight:600, textAlign:"left", transition:"all 0.2s"}}>
              <span style={{color: activeTab===t.id ? THEME.accent : THEME.textMuted}}>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        <div style={{padding: 20, borderTop:`1px solid ${THEME.border}`}}>
          <div style={{fontSize:12, color:THEME.textMuted, marginBottom:12}}>Eingeloggt als <b style={{color:THEME.text}}>{user.name}</b></div>
          <button onClick={logout} style={{width:"100%", background:"transparent", border:`1px solid ${THEME.border}`, color:THEME.warn, padding:"10px", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", gap:8, cursor:"pointer", fontWeight:600}}><LogOut size={16}/> Logout</button>
        </div>
      </div>

      {/* MAIN AREA */}
      <div style={{flex:1, display:"flex", flexDirection:"column"}}>
        <div style={{height: 70, borderBottom:`1px solid ${THEME.border}`, display:"flex", alignItems:"center", padding:"0 32px"}}>
          <h2 style={{margin:0, fontSize:18, fontWeight:600, letterSpacing:"0.05em"}}>
            {activeTab === "deploy" && "FLEET DEPLOYMENT & UPDATES"}
            {activeTab === "broadcast" && "GLOBAL BROADCAST & NEWS"}
            {activeTab === "radar" && "TELEMETRY RADAR"}
          </h2>
        </div>

        <div style={{padding: 32, flex:1, overflowY:"auto"}}>
          
          {/* TAB: DEPLOYMENT */}
          {activeTab === "deploy" && (
            <div style={{maxWidth: 900, display:"flex", flexDirection:"column", gap: 24}}>
              <div style={{background:THEME.panel, border:`1px solid ${THEME.border}`, borderRadius:12, padding:24}}>
                <h3 style={{margin:"0 0 8px 0", display:"flex", alignItems:"center", gap:8}}><Zap color={THEME.accent} size={20}/> Update konfigurieren</h3>
                <p style={{fontSize:13, color:THEME.textMuted, marginBottom:20}}>Pusht den aktuellen Code (var/www/taskflow-test) auf das Live-System via rsync.</p>
                
                <div style={{display:"flex", gap:16, alignItems:"flex-end", flexWrap:"wrap"}}>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    <label style={{fontSize:11, fontWeight:700, color:THEME.textMuted, letterSpacing:"0.05em"}}>GEPLANTE UHRZEIT (OPTIONAL)</label>
                    <input type="datetime-local" value={deployTime} onChange={e=>setDeployTime(e.target.value)} style={{background:"#000", border:`1px solid ${THEME.border}`, color:THEME.text, padding:"12px", borderRadius:8, outline:"none"}} />
                  </div>
                  
                  <div style={{flex:1, display:"flex", justifyContent:"flex-end", gap:12}}>
                    {deployTime && <button onClick={()=>setDeployTime("")} style={{background:"transparent", border:`1px solid ${THEME.border}`, color:THEME.textMuted, padding:"12px 20px", borderRadius:8, cursor:"pointer", fontWeight:600}}>Reset</button>}
                    <button onClick={executeDeployment} style={{background:THEME.accent, border:"none", color:"#000", padding:"12px 24px", borderRadius:8, cursor:"pointer", fontWeight:800, display:"flex", alignItems:"center", gap:8, boxShadow:`0 0 15px ${THEME.accent}40`}}>
                      <Terminal size={18}/> {deployTime ? "UPDATE PLANEN" : "UPDATE SOFORT STARTEN"}
                    </button>
                  </div>
                </div>
              </div>

              {/* LIVE TERMINAL */}
              <div style={{background:"#000", border:`1px solid ${THEME.border}`, borderRadius:12, overflow:"hidden", display:"flex", flexDirection:"column"}}>
                <div style={{background:THEME.panel, padding:"10px 16px", borderBottom:`1px solid ${THEME.border}`, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <div style={{display:"flex", gap:6}}>
                    <div style={{width:12, height:12, borderRadius:"50%", background:THEME.warn}}/>
                    <div style={{width:12, height:12, borderRadius:"50%", background:"#F59E0B"}}/>
                    <div style={{width:12, height:12, borderRadius:"50%", background:THEME.success}}/>
                  </div>
                  <div style={{fontSize:12, color:THEME.textMuted, fontFamily:"monospace"}}>root@mothership:~# update-live.sh</div>
                  <button onClick={()=>setDeployLogs([])} style={{background:"transparent", border:"none", color:THEME.textMuted, cursor:"pointer", fontSize:18}} title="Clear">×</button>
                </div>
                
                <div ref={terminalRef} style={{height: 400, overflowY:"auto", padding:20, fontFamily:"'DM Mono', monospace, Consolas", fontSize:13, lineHeight:1.6, color:"#A8E6CF"}}>
                  {deployLogs.length === 0 ? (
                    <div style={{color:THEME.textMuted, opacity:0.5}}>Warte auf Deployment-Befehl...</div>
                  ) : (
                    deployLogs.map((log, i) => (
                      <div key={i} style={{color: log.includes("[ERROR]") ? THEME.warn : log.includes("[SYSTEM]") ? THEME.accent : "inherit", whiteSpace:"pre-wrap"}}>{log}</div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB: BROADCAST */}
          {activeTab === "broadcast" && (
            <div style={{maxWidth: 900, display:"flex", flexDirection:"column", gap: 24, animation: "fadeUp 0.3s ease"}}>
              <div style={{background:THEME.panel, border:`1px solid ${THEME.border}`, borderRadius:12, padding:24}}>
                <h3 style={{margin:"0 0 8px 0", display:"flex", alignItems:"center", gap:8}}><Radio color="#8B5CF6" size={20}/> Neue System-Nachricht</h3>
                <p style={{fontSize:13, color:THEME.textMuted, marginBottom:24}}>Veröffentlicht eine Neuigkeit im "News"-Tab aller Kunden und wahlweise als roter Live-Ticker am oberen Bildschirmrand.</p>
                
                <div style={{display:"flex", flexDirection:"column", gap:16}}>
                  <div style={{display:"flex", gap:16, flexWrap:"wrap"}}>
                    <div style={{flex:1, minWidth:280}}>
                      <div style={{fontSize:10, color:THEME.textMuted, marginBottom:6, fontWeight:700}}>TITEL DER NACHRICHT</div>
                      <input type="text" value={broadcastForm.title} onChange={e=>setBroadcastForm({...broadcastForm, title: e.target.value})} placeholder="z.B. TaskFlow Version 1.5 ist live!" style={{width:"100%", background:"#000", border:`1px solid ${THEME.border}`, color:THEME.text, padding:"12px", borderRadius:6, outline:"none"}} />
                    </div>
                    <div>
                      <div style={{fontSize:10, color:THEME.textMuted, marginBottom:6, fontWeight:700}}>ART DER MELDUNG</div>
                      <select value={broadcastForm.type} onChange={e=>setBroadcastForm({...broadcastForm, type: e.target.value})} style={{width:"100%", background:"#000", border:`1px solid ${THEME.border}`, color:THEME.text, padding:"11px 12px", borderRadius:6, outline:"none", cursor:"pointer"}}>
                        <option value="info">ℹ️ Info</option>
                        <option value="update">🚀 Update</option>
                        <option value="alert">⚠️ Warnung / Wartung</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <div style={{fontSize:10, color:THEME.textMuted, marginBottom:6, fontWeight:700}}>INHALT (Details)</div>
                    <textarea value={broadcastForm.message} onChange={e=>setBroadcastForm({...broadcastForm, message: e.target.value})} placeholder="Beschreibe die Änderungen oder Informationen genauer..." style={{width:"100%", background:"#000", border:`1px solid ${THEME.border}`, color:THEME.text, padding:"12px", borderRadius:6, outline:"none", minHeight:120, resize:"vertical", fontFamily:"inherit"}} />
                  </div>

                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8, padding:"12px", background:"#000", border:`1px solid ${THEME.border}`, borderRadius:8, flexWrap:"wrap", gap:12}}>
                    <label style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:13, fontWeight:600}}>
                      <input type="checkbox" checked={broadcastForm.show_in_ticker} onChange={e=>setBroadcastForm({...broadcastForm, show_in_ticker: e.target.checked})} style={{width:16, height:16, accentColor:THEME.accent}} />
                      Zusätzlich als Eilmeldung im Live-Ticker anzeigen
                    </label>
                    <button onClick={async () => {
                      if(!broadcastForm.title) return notify("Titel fehlt!");
                      try {
                        const res = await apiFetch("/admin/broadcast", { method:"POST", body: JSON.stringify(broadcastForm) }, token);
                        setPastBroadcasts([res.broadcast, ...pastBroadcasts]);
                        notify("Nachricht weltweit gesendet! 🚀");
                        setBroadcastForm({ title: "", message: "", type: "info", show_in_ticker: true });
                      } catch(e) { notify("Fehler: " + e.message); }
                    }} style={{background:"#8B5CF6", color:"#fff", border:"none", padding:"10px 24px", borderRadius:6, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:8}}>
                      <Radio size={16}/> Senden
                    </button>
                  </div>
                </div>
              </div>

              {/* 🚀 LISTE DER VERGANGENEN BROADCASTS */}
              <div style={{marginTop: 16}}>
                <h3 style={{fontSize: 12, fontWeight: 700, color: THEME.textMuted, letterSpacing: "0.1em", marginBottom: 16}}>VERGANGENE MELDUNGEN</h3>
                <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                  {pastBroadcasts.length === 0 && <div style={{fontSize: 13, color: THEME.textMuted, background: THEME.panel, padding: "20px", borderRadius: 8, textAlign: "center", border: `1px solid ${THEME.border}`}}>Keine Nachrichten vorhanden.</div>}
                  
                  {pastBroadcasts.map(b => (
                    <div key={b.id} style={{background: THEME.panel, border: `1px solid ${THEME.border}`, padding: '16px 20px', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:4}}>
                          <span style={{fontSize: 15, fontWeight: 700, color: THEME.text}}>{b.title}</span>
                          {b.show_in_ticker && <span style={{fontSize:9, background:"#FF475720", color:"#FF4757", padding:"2px 6px", borderRadius:4, fontWeight:700}}>TICKER</span>}
                        </div>
                        <div style={{fontSize: 12, color: THEME.textMuted}}>
                          <span style={{textTransform: "uppercase"}}>{b.type}</span> • {new Date(b.created_at).toLocaleString('de-DE')}
                        </div>
                      </div>
                      <button onClick={async () => {
                        if(!window.confirm("Soll diese Nachricht bei allen Kunden gelöscht werden?")) return;
                        try {
                          await apiFetch(`/admin/broadcast/${b.id}`, { method: 'DELETE' }, token);
                          setPastBroadcasts(prev => prev.filter(x => x.id !== b.id));
                          notify("Nachricht global gelöscht 🗑️");
                        } catch(e) { notify("Fehler: " + e.message); }
                      }} style={{background: 'rgba(255, 71, 87, 0.1)', color: '#FF4757', border: '1px solid rgba(255, 71, 87, 0.3)', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight:600}} className="hov">
                        <Trash2 size={16}/> Löschen
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

      {/* TAB: REMOTE CONFIG (FEATURE TOGGLES) */}
          {activeTab === "toggles" && (
            <div style={{maxWidth: 900, display:"flex", flexDirection:"column", gap: 24, animation: "fadeUp 0.3s ease"}}>
              <div>
                <h3 style={{margin:"0 0 8px 0", display:"flex", alignItems:"center", gap:8}}><ToggleRight color={THEME.accent} size={20}/> Remote Feature Toggles</h3>
                <p style={{fontSize:13, color:THEME.textMuted}}>Aktiviere oder deaktiviere Module (Dev & Releases, Beta-Portal etc.) live für bestimmte Instanzen.</p>
              </div>

              {remoteNodes.length === 0 && <div style={{color:THEME.textMuted}}>Lade Instanzen...</div>}

              {remoteNodes.map((node, i) => (
                <div key={i} style={{background:THEME.panel, border:`1px solid ${THEME.border}`, borderRadius:12, padding:24}}>
                  <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:20}}>
                    <Server size={20} color={node.url === "local" ? "#8B5CF6" : THEME.accent} />
                    <span style={{fontSize:16, fontWeight:700}}>{node.instance_name}</span>
                    <span style={{fontSize:10, background:THEME.border, padding:"3px 8px", borderRadius:6, color:THEME.textMuted}}>{node.url}</span>
                  </div>

                  {node.error ? (
                    <div style={{color:THEME.warn, fontSize:13}}>⚠️ Instanz blockiert oder offline.</div>
                  ) : (
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
                      {[
                        { key: "feature_nl2", label: "Dev & Releases Modul" },
                        { key: "feature_roadmap", label: "Roadmap Modul" },
                        { key: "feature_beta", label: "Beta & QA Modul" },
                        { key: "feature_polls", label: "Umfragen Modul" },
                        { key: "feature_discord", label: "Discord Bot Integration" },
                        { key: "feature_reservation", label: "Projekt-Reservierung (Locks)" },
  			{ key: "feature_wiki", label: "Wiki & Docs Modul" }
                      ].map(f => (
                        <div key={f.key} style={{display:"flex", justifyContent:"space-between", alignItems:"center", background:"#000", padding:"12px 16px", borderRadius:8, border:`1px solid ${THEME.border}`}}>
                          <span style={{fontSize:13, fontWeight:600}}>{f.label}</span>
                          <button onClick={async () => {
                            const newFeatures = { ...node.features, [f.key]: !node.features[f.key] };
                            // UI sofort updaten (optimistisch)
                            setRemoteNodes(prev => prev.map(n => n.url === node.url ? {...n, features: newFeatures} : n));
                            try {
                              await apiFetch("/admin/clients/features", { method: "PUT", body: JSON.stringify({ url: node.url, features: newFeatures }) }, token);
                              notify("Modul live umgeschaltet! 🎛️");
                            } catch(e) { notify("Fehler: " + e.message); }
                          }} style={{width:44, height:24, borderRadius:12, background: node.features[f.key] !== false ? THEME.success : THEME.border, border:"none", position:"relative", cursor:"pointer", transition:"background 0.2s"}}>
                            <div style={{position:"absolute", top:2, left: node.features[f.key] !== false ? 22 : 2, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"all 0.2s", boxShadow:"0 2px 5px rgba(0,0,0,0.3)"}}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

      {/* TAB: GLOBAL ANALYTICS */}
          {activeTab === "analytics" && (
            <div style={{maxWidth: 1000, display:"flex", flexDirection:"column", gap: 24, animation: "fadeUp 0.3s ease"}}>
              <div>
                <h3 style={{margin:"0 0 8px 0", display:"flex", alignItems:"center", gap:8}}><Globe color={THEME.success} size={20}/> Globale SaaS Kennzahlen</h3>
                <p style={{fontSize:13, color:THEME.textMuted}}>Aggregierte Business-Metriken über alle verbundenen TaskFlow-Instanzen weltweit.</p>
              </div>

              {!analyticsData ? (
                <div style={{color:THEME.textMuted}}>Berechne weltweite Daten...</div>
              ) : (
                <>
                  {/* GLOBALE KPIs */}
                  <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:20}}>
                    {[
                      { label: "NUTZER WELTWEIT", value: analyticsData.totals.users, color: THEME.accent },
                      { label: "PROJEKTE GESAMT", value: analyticsData.totals.projects, color: "#8B5CF6" },
                      { label: "ERSTELLTE AUFGABEN", value: analyticsData.totals.tasks, color: "#F59E0B" },
                      { label: "ABGESCHLOSSEN", value: analyticsData.totals.tasks_done, color: THEME.success }
                    ].map((stat, i) => (
                      <div key={i} style={{background:THEME.panel, border:`1px solid ${THEME.border}`, borderRadius:16, padding:24, boxShadow:"0 4px 12px rgba(0,0,0,0.02)"}}>
                        <div style={{fontSize:11, color:THEME.textGhost, fontWeight:800, letterSpacing:"0.1em", marginBottom:12}}>{stat.label}</div>
                        <div style={{fontSize:36, fontWeight:800, color:stat.color, fontFamily:"'DM Mono', monospace"}}>{stat.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* AUFSCHLÜSSELUNG PRO INSTANZ */}
                  <div style={{marginTop: 16}}>
                    <h3 style={{fontSize: 12, fontWeight: 700, color: THEME.textMuted, letterSpacing: "0.1em", marginBottom: 16}}>AUFSCHLÜSSELUNG NACH INSTANZ</h3>
                    <div style={{background:THEME.panel, border:`1px solid ${THEME.border}`, borderRadius:16, overflow:"hidden"}}>
                      <table style={{width:"100%", borderCollapse:"collapse", textAlign:"left", fontSize:13}}>
                        <thead>
                          <tr style={{background:"#000", color:THEME.textMuted}}>
                            <th style={{padding:"14px 20px", fontWeight:600, borderBottom:`1px solid ${THEME.border}`}}>Instanz</th>
                            <th style={{padding:"14px 20px", fontWeight:600, borderBottom:`1px solid ${THEME.border}`}}>URL</th>
                            <th style={{padding:"14px 20px", fontWeight:600, borderBottom:`1px solid ${THEME.border}`}}>Nutzer</th>
                            <th style={{padding:"14px 20px", fontWeight:600, borderBottom:`1px solid ${THEME.border}`}}>Projekte</th>
                            <th style={{padding:"14px 20px", fontWeight:600, borderBottom:`1px solid ${THEME.border}`}}>Aufgaben (Erledigt)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.instances.map((inst, i) => {
                            const pct = inst.tasks > 0 ? Math.round((inst.tasks_done / inst.tasks) * 100) : 0;
                            return (
                              <tr key={i} style={{borderBottom: i < analyticsData.instances.length-1 ? `1px solid ${THEME.border}` : 'none'}}>
                                <td style={{padding:"16px 20px", fontWeight:700, color:THEME.text}}>
                                  {inst.error ? <span style={{color:THEME.warn}}>⚠️ {inst.error}</span> : inst.instance_name}
                                </td>
                                <td style={{padding:"16px 20px", color:THEME.textMuted}}>{inst.url}</td>
                                <td style={{padding:"16px 20px", color:THEME.accent, fontWeight:600, fontFamily:"monospace"}}>{inst.users}</td>
                                <td style={{padding:"16px 20px", color:"#8B5CF6", fontWeight:600, fontFamily:"monospace"}}>{inst.projects}</td>
                                <td style={{padding:"16px 20px"}}>
                                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                                    <div style={{width: 60, height: 6, background: "#000", borderRadius: 3, overflow: "hidden"}}>
                                      <div style={{width: `${pct}%`, height: "100%", background: THEME.success}} />
                                    </div>
                                    <span style={{color:THEME.textSub, fontFamily:"monospace"}}>{inst.tasks_done} / {inst.tasks}</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 📡 TELEMETRY RADAR TAB */}
          {activeTab === "radar" && (
            <div style={{display:"flex", flexDirection:"column", gap: 24, animation: "fadeUp 0.3s ease"}}>
              
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div>
                  <h3 style={{margin:"0 0 8px 0", display:"flex", alignItems:"center", gap:8}}><Activity color={THEME.success} size={20}/> Flotten-Telemetrie</h3>
                  <p style={{fontSize:13, color:THEME.textMuted, margin:0}}>Live-Verbindung zu allen angebundenen Instanzen ({radarLoading ? "Scanne..." : "Synchronisiert"}).</p>
                </div>
                {radarLoading && <div style={{width:16, height:16, borderRadius:"50%", background:THEME.success, boxShadow:`0 0 12px ${THEME.success}`, animation:"pulse 1.5s infinite"}} />}
              </div>

              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 20}}>
                {radarNodes.length === 0 && !radarLoading && <div style={{color:THEME.textMuted}}>Keine Sensordaten empfangen.</div>}
                
                {radarNodes.map((node, idx) => (
                  <div key={idx} style={{background:THEME.panel, border:`1px solid ${node.offline ? THEME.warn : node.is_mothership ? "#8B5CF6" : THEME.border}`, borderRadius:16, padding:24, position:"relative", overflow:"hidden"}}>
                    
                    {/* Hintergrund-Glühen für Mutterschiff */}
                    {node.is_mothership && <div style={{position:"absolute", top:-50, right:-50, width:150, height:150, background:"radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)"}} />}
                    
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, position:"relative", zIndex:2}}>
                      <div>
                        <div style={{fontSize:16, fontWeight:800, color:THEME.text, display:"flex", alignItems:"center", gap:8}}>
                          {node.offline ? <ShieldAlert size={18} color={THEME.warn}/> : <Server size={18} color={node.is_mothership ? "#8B5CF6" : THEME.accent}/>}
                          {node.instance_name}
                        </div>
                        <div style={{fontSize:11, color:THEME.textMuted, marginTop:4, fontWeight:700, letterSpacing:"0.05em"}}>
                          {node.offline ? "OFFLINE / NICHT ERREICHBAR" : (node.is_mothership ? "COMMAND CENTER (LOKAL)" : "KUNDEN-INSTANZ (LIVE)")}
                        </div>
                      </div>
                      <div style={{padding:"4px 10px", borderRadius:6, background: node.offline ? "#FF475720" : "#34D39920", color: node.offline ? THEME.warn : THEME.success, fontSize:11, fontWeight:800}}>
                        {node.offline ? "OFFLINE" : "ONLINE"}
                      </div>
                    </div>

                    {node.offline ? (
                      <div style={{color:THEME.warn, fontSize:13, background:"#FF475710", padding:"12px", borderRadius:8, border:`1px dashed ${THEME.warn}50`}}>
                        ⚠️ Verbindung fehlgeschlagen: {node.error}
                      </div>
                    ) : (
                      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20}}>
                        
                        {/* CPU */}
                        <div>
                          <div style={{display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:700, color:THEME.textMuted, marginBottom:6}}>
                            <span>CPU</span> <span style={{color: node.cpu_percent > 80 ? THEME.warn : THEME.text}}>{node.cpu_percent}%</span>
                          </div>
                          <div style={{height:6, background:"#000", borderRadius:4, overflow:"hidden"}}>
                            <div style={{height:"100%", width:`${node.cpu_percent}%`, background: node.cpu_percent > 80 ? THEME.warn : THEME.accent, transition:"width 0.5s ease"}}/>
                          </div>
                        </div>

                        {/* RAM */}
                        <div>
                          <div style={{display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:700, color:THEME.textMuted, marginBottom:6}}>
                            <span>RAM ({node.ram_used_gb} / {node.ram_total_gb} GB)</span> <span style={{color: node.ram_percent > 85 ? THEME.warn : THEME.text}}>{node.ram_percent}%</span>
                          </div>
                          <div style={{height:6, background:"#000", borderRadius:4, overflow:"hidden"}}>
                            <div style={{height:"100%", width:`${node.ram_percent}%`, background: node.ram_percent > 85 ? THEME.warn : "#8B5CF6", transition:"width 0.5s ease"}}/>
                          </div>
                        </div>

                        {/* ONLINE USERS */}
                        <div style={{background:"#000", padding:"12px", borderRadius:8, border:`1px solid ${THEME.border}`, gridColumn:"1", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                          <div style={{fontSize:10, color:THEME.textMuted, fontWeight:700}}>ONLINE NUTZER</div>
                          <div style={{fontSize:24, fontWeight:800, color:THEME.success, fontFamily:"monospace"}}>{node.online_users}</div>
                        </div>

                        {/* DB SIZE */}
                        <div style={{background:"#000", padding:"12px", borderRadius:8, border:`1px solid ${THEME.border}`, gridColumn:"2", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                          <div style={{fontSize:10, color:THEME.textMuted, fontWeight:700}}>DATENBANK GRÖSSE</div>
                          <div style={{fontSize:18, fontWeight:800, color:THEME.text, fontFamily:"monospace", marginTop:4}}>{node.db_size_mb} MB</div>
                        </div>

                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}